<template>
  <div class="predictive-search-results grid">
    <div class="predictive-search-results__aside col xs-span l2">
      <div class="predictive-search-results__blocks">
        <transition name="fade">
          <div
            v-if="suggestions.length"
            class="predictive-search-results__block"
          >
            <h3
              class="predictive-search-results__title text-body-m-bold"
              v-text="$string('predictive_search.suggestions')"
            />

            <ul class="predictive-search-results__list">
              <li
                v-for="suggestion of suggestions.slice(0, 3)"
                :key="suggestion"
              >
                <a
                  :href="generateSuggestionLink(suggestion)"
                  class="predictive-search-results__item text-body-s-regular"
                  v-html="highlightText(suggestion.text)"
                />
              </li>
            </ul>
          </div>
        </transition>

        <transition name="fade">
          <div
            v-if="isMobile && products.length"
            class="predictive-search-results__block"
          >
            <div class="predictive-search-results__title-group">
              <h3
                class="text-body-m-bold"
                v-text="$string('predictive_search.products')"
              />
              <a
                class="text-body-s-underline"
                :href="`${$variable('routes.search')}?q=${searchQuery}&type=product`"
                v-text="$string('predictive_search.all_products')"
              />
            </div>

            <div class="predictive-search-results__product-list">
              <product-card
                v-for="product of products"
                :key="product.id"
                :loaded-animation="false"
                :show-wishlist-icon="false"
                :show-ratings="false"
                :show-badges="false"
                :show-quick-add="false"
                inline
                :product="product"
                sizes="(min-width: 1024px) 33vw, 282px"
              />
            </div>
          </div>
        </transition>

        <transition name="fade">
          <div
            v-if="collections.length"
            class="predictive-search-results__block"
          >
            <h3
              class="predictive-search-results__title text-body-m-bold"
              v-text="$string('predictive_search.collections')"
            />

            <ul class="predictive-search-results__list">
              <li
                v-for="collection of collections.slice(0, 3)"
                :key="collection.handle"
              >
                <a
                  :href="collection.url"
                  class="predictive-search-results__item text-body-s-regular"
                  v-html="highlightText(collection.title)"
                />
              </li>
            </ul>
          </div>
        </transition>

        <transition name="fade">
          <div
            v-if="articles.length"
            class="predictive-search-results__block"
          >
            <h3
              class="predictive-search-results__title text-body-m-bold"
              v-text="$string('predictive_search.articles')"
            />

            <ul class="predictive-search-results__list">
              <li
                v-for="article of articles.slice(0, 3)"
                :key="article.handle"
              >
                <a
                  :href="article.onlineStoreUrl"
                  class="predictive-search-results__item text-body-s-regular"
                  v-html="highlightText(article.title)"
                />
              </li>
            </ul>
          </div>
        </transition>

        <transition name="fade">
          <div
            v-if="pages.length"
            class="predictive-search-results__block"
          >
            <h3
              class="predictive-search-results__title text-body-m-bold"
              v-text="$string('predictive_search.pages')"
            />

            <ul class="predictive-search-results__list">
              <li
                v-for="page of pages.slice(0, 3)"
                :key="page.handle"
              >
                <a
                  :href="page.onlineStoreUrl"
                  class="predictive-search-results__item text-body-s-regular"
                  v-html="highlightText(page.title)"
                />
              </li>
            </ul>
          </div>
        </transition>
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="!isMobile && products.length"
        class="col xs-span l4-12"
      >
        <div class="predictive-search-results__title-group">
          <h3
            class="text-body-m-bold"
            v-text="$string('predictive_search.products')"
          />
          <a
            class="text-body-s-underline"
            :href="`${$variable('routes.search')}?q=${searchQuery}&type=product`"
            v-text="$string('predictive_search.all_products')"
          />
        </div>

        <content-carousel
          :cards="products"
          :container="false"
          :show-wishlist-icon="false"
          :show-ratings="false"
          card-type="product"
          class="predictive-search-results__list"
        />
      </div>
    </transition>
  </div>
</template>

<script>

/**
 * Vue: Predictive search results (predictive-search-results)
 * -----------------------------------------------------------------------------
 * Component to display predictive search results.
 *
 * @param {Array} articles - Array of article search results.
 * @param {Array} collections - Array of collection search results.
 * @param {Array} pages - Array of page search results.
 * @param {Array} products - Array of product search results.
 * @param {Array} suggestions - Array of suggestions from predictive search.
 * @param {String} searchQuery - Current search query.
 *
 */
import { mapState } from 'vuex'

import ContentCarousel from '~async/content-carousel/content-carousel.vue'
import ProductCard from '~global/product-card/product-card'

export default {
  name: 'PredictiveSearchResults',

  components: {
    ContentCarousel,
    ProductCard,
  },

  props: {
    articles: {
      type: Array,
      default: () => [],
    },
    collections: {
      type: Array,
      default: () => [],
    },
    pages: {
      type: Array,
      default: () => [],
    },
    products: {
      type: Array,
      default: () => [],
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Create a regex for matching highlight text.
     */
    searchQueryRegex() {
      return new RegExp(this.searchQuery, 'gi')
    },
  },

  methods: {

    /**
     * Returns highlighted text from the recommendation string.
     * @returns {String}
     */
    highlightText(text) {
      return text.replace(this.searchQueryRegex, (match) => `<span class="highlight text-body-s-bold">${match}</span>`)
    },

    /**
     * Returns a link from suggestion text and tracking parameters.
     * @returns {String}
     */
    generateSuggestionLink(suggestion) {
      return `${this.$variable('routes.search')}?q=${suggestion.text}&type=product&${suggestion.trackingParameters}`
    },
  },
}
</script>
